import { Formik, useField, useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { Heading } from 'blaze-ui/Heading/Heading'
import { TitlesLoadingState } from 'components/Wizards/BlogPostWizard/TitlesLoadingState'
import { Radio, RadioGroup } from 'components/RadioGroup'
import Styles from 'components/Wizards/BlogPostWizard/BlogPostWizard.module.scss'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { ArrowRight, Edit } from 'almanac-ui/icons'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Uuid, Values } from 'components/Wizards/MultiStepBlogPostWizard/Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { useTitleOptions } from 'components/Wizards/MultiStepBlogPostWizard/TitleOptionsProvider'
import { useGenerateTitleOptions } from 'components/Wizards/MultiStepBlogPostWizard/useGenerateTitleOptions'
import { useGenerateOutline } from 'components/Wizards/MultiStepBlogPostWizard/useGenerateOutline'
import { useGenerateIntro } from 'components/Wizards/MultiStepBlogPostWizard/useGenerateIntro'

interface Props extends Pick<StepProps, 'onBack'> {}

const SelectTitle = ({ onBack }: Props) => {
  const { values, submitForm } = useFormikContext<Values>()
  const { titleOptions, abortGeneration, updateTitleOption, addTitleOption, isLoading } = useTitleOptions()

  const [{ value: selectedTitleId }, , { setValue: setSelectedTitleId }] = useField<Uuid>('selected_title_id')
  const { isMobile } = useResponsiveDesign()
  const generateTitleOptions = useGenerateTitleOptions({ onCancel: onBack })

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields>
            <Heading level={4} id="wizard-header">
              Pick a title for your post, or write your own
            </Heading>
            {isLoading && <TitlesLoadingState />}
            {!isLoading && (
              <RadioGroup
                aria-label="Title"
                value={selectedTitleId}
                className={Styles.radioGroup}
                onChange={(selectedUuid) => {
                  setSelectedTitleId(selectedUuid)
                }}
              >
                {titleOptions.map((titleOption, i) => {
                  const [uuid, value] = titleOption
                  return (
                    <Radio className={Styles.titleOption} value={uuid} aria-label={value} key={uuid} hideRadio>
                      {(radioBulletProps) => (
                        <>
                          <div />
                          <div className={Styles.inputLabel}>Title #{i + 1}</div>
                          <div>
                            <RadioBullet {...radioBulletProps} size={20} />
                          </div>
                          <div>
                            <Input
                              isTextarea
                              scrollable={false}
                              className={Styles.boldInput}
                              size="lg"
                              name={`Title ${i + 1}`}
                              value={value}
                              onChange={(e) => updateTitleOption([uuid, e.target.value])}
                              autoFocus={value === ''}
                              aria-label="Generated Title"
                              placeholder="Write your own title..."
                              onKeyDown={(e) => {
                                // Prevent up and down from selecting other radio options
                                e.stopPropagation()
                              }}
                            />
                          </div>
                        </>
                      )}
                    </Radio>
                  )
                })}
              </RadioGroup>
            )}
            <div className={Styles.titleOptionsFieldsActions}>
              <Button variant="tertiary" size="lg" frontIcon={Edit} onPress={addTitleOption} isDisabled={isLoading}>
                Write Your Own
              </Button>
            </div>

            {values.seo_words.length > 0 && (
              <div className={Styles.titleOptionsFieldsFooter}>
                <div className={Styles.inputLabel}>Your SEO keywords we tried to include in the above titles</div>
                <div className={Styles.seoWordsChips}>
                  {values.seo_words.map((seoWord, i) => (
                    <span key={`seoWord-${i}`} className={Styles.seoWordChip}>
                      {seoWord}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>
      <Form.Actions isSticky>
        <ResponsiveDesign.Desktop>
          <Form.PreviousButton
            onPress={() => {
              abortGeneration()
              onBack?.()
            }}
          >
            Previous Step
          </Form.PreviousButton>
        </ResponsiveDesign.Desktop>
        <Form.PrimaryActions>
          <Button
            variant="tertiary"
            size="lg"
            onPress={() => generateTitleOptions(values)}
            isDisabled={isLoading}
            fullWidth={isMobile}
          >
            Generate New Titles
          </Button>
          <Button
            size="lg"
            onPress={() => {
              submitForm()
            }}
            endIcon={ArrowRight}
            isDisabled={isLoading}
            fullWidth={isMobile}
          >
            Generate Outline
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}

export const SelectTitleContainer = multiStep(({ schema, onSubmit, onBack, initialValues }: StepProps) => {
  const { titleOptions } = useTitleOptions()
  const generateOutline = useGenerateOutline({ onCancel: onBack })
  const generateIntro = useGenerateIntro({ onCancel: onBack })
  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        const { selected_title_id: selectedTitleId, ...submitValues } = values as Values & { selected_title_id: Uuid }
        const title = titleOptions.find(([uuid]) => uuid === selectedTitleId)?.[1] || ''
        generateOutline({
          ...submitValues,
          title,
        })
        generateIntro({ ...submitValues, title })
        return onSubmit({ ...submitValues, title })
      }}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <SelectTitle onBack={onBack} />
    </Formik>
  )
})

export { SelectTitleContainer as SelectTitle }
